import { ApiError, createIDTagging, useApiClient, useIDTagging, useTranslation } from '@msdk/common'
import {
  EntityMetadata,
  ListValue,
  readBaseMetadataByName,
  StringFieldDefinition,
  StringValue,
} from '@msdk/definitions'
import { FieldProps, useEntityFromCollection, useFieldSeverity, useFieldValue, ValidationMessages } from '@msdk/forms'
import { FormField, FormFieldLabel, MenuItem, StyledSelect } from '@msdk/ui'
import React, { useEffect, useState } from 'react'

import { EntityNumericFields } from '../../../ClientCustomLogics/measurement-unit/measurement-unit-logic'

export function MuEntityFieldSelector({ node, onChange }: FieldProps) {
  const t = useTranslation()
  const fieldDef = node.value.fieldDefinition as StringFieldDefinition
  const client = useApiClient()
  const entityContext = useEntityFromCollection(node.value.entityId as string)
  const idTaggingEnabled = useIDTagging()

  const [value, setValue] = useFieldValue(node, onChange)
  const [supportedFieldsForEntity, setSupportedFieldsForEntity] = useState<TranslatedDefinition[]>([])
  const [allFieldsFromMetadata, setAllFieldsFromMetadata] = useState<EntityNumericFields[]>([])

  useEffect(() => {
    const fetch = async () => {
      const res = await client.read<EntityNumericFields[]>('CMPS/api/composer/basemetadatawithnumerics')
      setAllFieldsFromMetadata(res instanceof ApiError ? [] : (res as EntityNumericFields[]))
    }
    fetch().catch(console.error)
  }, [])

  useEffect(() => {
    const fetch = async () => {
      const metadataList: ListValue = entityContext?.fields['METADATA_LIST'] as ListValue

      if (!!metadataList && metadataList.value && metadataList.value.length > 0) {
        const entityName: StringValue = metadataList.value[0].fields['METADATA'] as StringValue

        if (!!entityName && !entityName.isEmpty()) {
          const baseMetadata = readBaseMetadataByName(entityName.value!) as EntityMetadata
          const allFields = [...allFieldsFromMetadata]
          const fields: TranslatedDefinition[] | undefined = allFields
            .find((x) => x.entity.name == entityName.value)
            ?.numericFields.map((n) => {
              return {
                id: n.idml,
                translatedDescription: n.tDescription ?? n.description, //cg Translation is coming from server
                value: n.name,
                idml: n.idml,
                microservice: baseMetadata.msProvider,
                entityName: entityName.value!,
              }
            })

          setSupportedFieldsForEntity(fields ? fields : [])
        }
      }
    }
    fetch().catch(console.error)
  }, [entityContext, allFieldsFromMetadata])

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any*/
  const onFieldChange = (e: any) => {
    const newValue = e.target.value as string
    const fieldValue = newValue && newValue !== '' ? new StringValue(newValue) : null
    node.value.isDirty = true
    setValue(fieldValue)
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)

  return (
    <FormField size="small" severity={severity} readOnly={isReadOnly} disabled={isReadOnly}>
      <FormFieldLabel>{t('FRM_MEASUREMENT_UNIT_FIELD_NAME', 'Measurement unit field')}</FormFieldLabel>
      <StyledSelect
        value={value ? value.value : ''}
        label={t('FRM_MEASUREMENT_UNIT_FIELD_NAME', 'Measurement unit field')}
        onChange={onFieldChange}
        disabled={isReadOnly}
      >
        <MenuItem value={undefined}> - </MenuItem>
        {supportedFieldsForEntity.map((f, index) => (
          <MenuItem key={index} value={f.value}>
            {idTaggingEnabled ? createIDTagging(f.microservice, f.entityName, f.idml) : f.translatedDescription}
          </MenuItem>
        ))}
      </StyledSelect>
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}

interface TranslatedDefinition {
  id: string
  value?: string
  translatedDescription: string
  idml: string
  microservice: string
  entityName: string
}
